import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Fingerprint,
  Inbox,
  RefreshCw,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import PageHeader from "../../../components/shared/PageHeader";
import { formatMelbourneDateTime } from "../../../utils/melbourneTime";
import {
  decidePropertyIdReview,
  getPropertyIdReviewMetrics,
  getPropertyIdReviews,
  searchPropertyIdCandidates,
  undoPropertyIdReview,
} from "../../../api/propertyIdReview";

/**
 * Property ID review queue (Property Brain Phase 2). The stamper only links a
 * record to a master Property ID on an exact or high-confidence match; what it
 * can't trust lands here for the data team. A decision is locked (the nightly
 * re-check leaves the record alone) and can be undone. Nothing merges on a
 * partial address: when unsure, "No match" keeps the record blank.
 */

const STATUS_TABS = [
  { id: "open", label: "Open" },
  { id: "resolved", label: "Resolved" },
  { id: "auto_closed", label: "Closed by re-check" },
];

const STORE_OPTIONS = [
  { value: "", label: "All records" },
  { value: "property", label: "CRM listings" },
  { value: "owner", label: "Owner Database" },
  { value: "homer", label: "Homer campaigns" },
  { value: "homer_key", label: "Homer unit keys" },
];

const REASON_OPTIONS = [
  { value: "", label: "All reasons" },
  { value: "untrusted", label: "Uncertain match" },
  { value: "unmatched", label: "No match" },
  { value: "changed", label: "ID changed on re-check" },
  { value: "cleared", label: "ID removed on re-check" },
  { value: "homer_unit", label: "Homer unit clash" },
];

const REASON_STYLES = {
  untrusted: "bg-amber-100 text-amber-800",
  unmatched: "bg-gray-100 text-gray-700",
  changed: "bg-red-100 text-red-700",
  cleared: "bg-orange-100 text-orange-800",
  homer_unit: "bg-purple-100 text-purple-700",
};

const PAGE_SIZE = 25;

const errorText = (e, fallback) => e?.response?.data?.error || e?.response?.data?.detail || fallback;

// "1/2 Maple Court" -> "1"; "Unit 2, 10 Smith St" -> "2".
export const unitFromAddress = (address) => {
  const a = (address || "").trim();
  const slash = a.match(/^(?:unit|apt|apartment|flat|shop|suite)?\s*(\d+[A-Za-z]?)\s*\/\s*\d/i);
  if (slash) return slash[1].toUpperCase();
  const word = a.match(/^(?:unit|apt|apartment|flat|shop|suite)\s+(\d+[A-Za-z]?)\b/i);
  return word ? word[1].toUpperCase() : "";
};

const propertyLine = (p) => {
  if (!p) return "";
  const parts = [p.address, [p.locality, p.state, p.postcode].filter(Boolean).join(" ")].filter(Boolean);
  return parts.join(", ");
};

const Pill = ({ className = "", children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${className}`}>
    {children}
  </span>
);

const Stat = ({ label, value, hint }) => (
  <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm">
    <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className="text-xl font-semibold text-gray-900">{value ?? "—"}</div>
    {hint && <div className="text-[11px] text-gray-400">{hint}</div>}
  </div>
);

const PropertyCard = ({ title, property, id, confidence, method }) => {
  if (!id) return null;
  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{title}</div>
      <div className="mt-1 text-sm font-medium text-gray-900">
        {property ? propertyLine(property) : `Property ${id}`}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
        <span>ID {id}</span>
        {property?.unit && <span>Unit {property.unit}</span>}
        {property?.gnaf_pid && <span>GNAF {property.gnaf_pid}</span>}
        {confidence && <span>Match: {confidence}{method ? ` (${method})` : ""}</span>}
      </div>
    </div>
  );
};

const recordLink = (item) => {
  if (item.store === "property" && item.record?.id) return `/properties/${item.record.id}`;
  return null;
};

const CandidatePicker = ({ initialQuery, onPick, picked }) => {
  const [q, setQ] = useState(initialQuery || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2 && !/^\d+$/.test(term)) {
      setResults([]);
      return undefined;
    }
    let live = true;
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await searchPropertyIdCandidates(term);
        if (live) setResults(data.results || []);
      } catch (e) {
        if (live) toast.error(errorText(e, "Search failed."));
      } finally {
        if (live) setLoading(false);
      }
    }, 350);
    return () => {
      live = false;
      clearTimeout(id);
    };
  }, [q]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search an address (with suburb) or type a Property ID"
          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
        />
      </div>
      {loading && <div className="text-xs text-gray-400">Searching…</div>}
      <div className="max-h-56 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-lg">
        {results.map((r) => (
          <button
            key={r.collings_property_id}
            type="button"
            onClick={() => onPick(r)}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
              picked?.collings_property_id === r.collings_property_id ? "bg-cyan-50" : ""
            }`}
          >
            <div className="font-medium text-gray-900">{propertyLine(r)}</div>
            <div className="text-xs text-gray-500">
              ID {r.collings_property_id}
              {r.unit ? ` · Unit ${r.unit}` : ""}
              {r.gnaf_pid ? ` · GNAF ${r.gnaf_pid}` : ""}
            </div>
          </button>
        ))}
        {!loading && results.length === 0 && q.trim().length >= 2 && (
          <div className="px-3 py-2 text-xs text-gray-400">No properties found. Include the suburb in the search.</div>
        )}
      </div>
    </div>
  );
};

const ReviewDetail = ({ item, onChanged, onClose }) => {
  const [mode, setMode] = useState(null); // choose | unit
  const [picked, setPicked] = useState(null);
  const [building, setBuilding] = useState(null);
  const [unit, setUnit] = useState(unitFromAddress(item.address));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setMode(null);
    setPicked(null);
    setBuilding(null);
    setUnit(unitFromAddress(item.address));
    setNote("");
  }, [item.id, item.address]);

  const act = async (label, fn) => {
    setBusy(true);
    try {
      const { data } = await fn();
      toast.success(label);
      onChanged(data);
    } catch (e) {
      toast.error(errorText(e, "That didn't work."));
    } finally {
      setBusy(false);
    }
  };

  const decide = (decision, extra = {}) =>
    act("Saved", () => decidePropertyIdReview(item.id, { decision, note, ...extra }));

  const isHomerKey = item.store === "homer_key";
  const link = recordLink(item);
  const btn = "px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Pill className="bg-cyan-50 text-cyan-800">{item.store_label}</Pill>
            <Pill className={REASON_STYLES[item.reason] || "bg-gray-100 text-gray-700"}>{item.reason_label}</Pill>
            {item.times_flagged > 1 && (
              <Pill className="bg-red-50 text-red-700">Back {item.times_flagged} times</Pill>
            )}
          </div>
          <h2 className="mt-2 text-base font-semibold text-gray-900">{item.address || item.object_id}</h2>
          <div className="mt-1 text-xs text-gray-500">
            Flagged {formatMelbourneDateTime(item.flagged_at)}
            {link && (
              <a href={link} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-[#2a9aa5] hover:underline">
                Open record <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {item.record?.campaign_id && <span className="ml-2">Homer campaign {item.record.campaign_id}</span>}
          </div>
        </div>
        <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
      </div>

      {item.record && item.record.exists === false && (
        <div className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">This record no longer exists.</div>
      )}

      {isHomerKey ? (
        <div className="space-y-2 text-sm">
          <div className="text-gray-700">{item.detail?.why}</div>
          {(item.detail?.addresses || []).length > 0 && (
            <ul className="list-disc pl-5 text-gray-600">
              {item.detail.addresses.map((a) => <li key={a}>{a}</li>)}
            </ul>
          )}
          {(item.detail?.new_keys || []).length > 0 && (
            <div className="text-xs text-gray-500">Possible keys: {item.detail.new_keys.join(", ")}</div>
          )}
        </div>
      ) : (
        <div className="grid gap-2">
          {item.previous_id && (
            <PropertyCard title={item.reason === "changed" ? "Was linked to" : "Lost its link to"}
              property={item.previous} id={item.previous_id} />
          )}
          {item.proposed_id ? (
            <PropertyCard
              title={item.reason === "changed" ? "Re-check moved it to" : "Proposed property"}
              property={item.proposed}
              id={item.proposed_id}
              confidence={item.candidate?.confidence}
              method={item.candidate?.method}
            />
          ) : (
            <div className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg p-3">
              The Property Brain has no proposal for this address.
            </div>
          )}
        </div>
      )}

      {item.status === "resolved" ? (
        <div className="space-y-3">
          <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900">
            <div className="flex items-center gap-2 font-medium">
              <CheckCircle2 className="w-4 h-4" /> {item.decision_label}
              {item.decided_id ? ` → Property ${item.decided_id}` : ""}
            </div>
            {item.decided && <div className="mt-1 text-emerald-800">{propertyLine(item.decided)}</div>}
            <div className="mt-1 text-xs text-emerald-700">
              {item.decided_by} · {formatMelbourneDateTime(item.decided_at)}
            </div>
            {item.note && <div className="mt-2 text-emerald-900">{item.note}</div>}
            {item.was_wrong_link && <div className="mt-2 text-xs text-red-700">This replaced a wrong link.</div>}
          </div>
          {item.can_undo && (
            <button type="button" disabled={busy} onClick={() => act("Decision undone", () => undoPropertyIdReview(item.id))}
              className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2`}>
              <RotateCcw className="w-4 h-4" /> Undo decision
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Note (optional): why you decided this"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
          />
          {isHomerKey ? (
            <button type="button" disabled={busy} onClick={() => decide("resolved")}
              className={`${btn} text-white`} style={{ backgroundColor: "#55C5D0" }}>
              Mark resolved
            </button>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={busy || !item.proposed_id} onClick={() => decide("confirm")}
                  className={`${btn} text-white`} style={{ backgroundColor: "#55C5D0" }}
                  title={item.proposed_id ? "" : "No proposal to confirm"}>
                  Confirm proposed
                </button>
                <button type="button" disabled={busy} onClick={() => setMode(mode === "choose" ? null : "choose")}
                  className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50`}>
                  Choose different
                </button>
                <button type="button" disabled={busy} onClick={() => setMode(mode === "unit" ? null : "unit")}
                  className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50`}>
                  Separate unit
                </button>
                <button type="button" disabled={busy} onClick={() => decide("no_match")}
                  className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50`}>
                  No match
                </button>
              </div>
              {mode === "choose" && (
                <div className="space-y-2 border-t border-gray-100 pt-3">
                  <CandidatePicker initialQuery={item.address} picked={picked} onPick={setPicked} />
                  <button type="button" disabled={busy || !picked}
                    onClick={() => decide("choose", { property_id: picked.collings_property_id })}
                    className={`${btn} text-white`} style={{ backgroundColor: "#55C5D0" }}>
                    Use {picked ? `Property ${picked.collings_property_id}` : "this property"}
                  </button>
                </div>
              )}
              {mode === "unit" && (
                <div className="space-y-2 border-t border-gray-100 pt-3">
                  <p className="text-xs text-gray-500">
                    Gives this record its own Property ID for the unit. The building is the one this address
                    matches in the national address file; if it doesn't match one, pick the building below
                    (the proposal above is only a guess, so it isn't used). A unit that already has an ID gets that one.
                  </p>
                  <CandidatePicker initialQuery={item.address} picked={building} onPick={setBuilding} />
                  {building && (
                    <div className="text-xs text-gray-600">
                      Building: {propertyLine(building)} (ID {building.collings_property_id})
                      <button type="button" onClick={() => setBuilding(null)} className="ml-2 text-[#2a9aa5] hover:underline">clear</button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Unit"
                      className="w-28 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]" />
                    <button type="button" disabled={busy || !unit.trim()}
                      onClick={() => decide("separate_unit", {
                        unit: unit.trim(),
                        ...(building ? { property_id: building.collings_property_id } : {}),
                      })}
                      className={`${btn} text-white`} style={{ backgroundColor: "#55C5D0" }}>
                      Give unit its own ID
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const PropertyIdReviewPage = () => {
  const [status, setStatus] = useState("open");
  const [store, setStore] = useState("");
  const [reason, setReason] = useState("");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => setPage(1), [status, store, reason, debounced]);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current; // a filter change can fire two loads; keep only the newest
    setLoading(true);
    setError(null);
    try {
      const params = { status, page, page_size: PAGE_SIZE };
      if (store) params.store = store;
      if (reason) params.reason = reason;
      if (debounced) params.search = debounced;
      const { data } = await getPropertyIdReviews(params);
      if (seq !== requestSeq.current) return;
      setItems(data.results || []);
      setCount(data.count || 0);
    } catch (e) {
      if (seq === requestSeq.current) setError(errorText(e, "Couldn't load the review queue."));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [status, store, reason, debounced, page]);

  const loadMetrics = useCallback(async () => {
    try {
      const { data } = await getPropertyIdReviewMetrics();
      setMetrics(data);
    } catch {
      setMetrics(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const onChanged = (updated) => {
    // Only replace the open panel if it still shows this item (the reviewer may
    // have moved on while the request was in flight).
    setSelected((cur) => (cur && cur.id === updated.id ? updated : cur));
    setItems((rows) => rows.map((r) => (r.id === updated.id ? updated : r)));
    loadMetrics();
  };

  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const turnaround = useMemo(() => {
    const h = metrics?.turnaround_hours_median;
    if (h == null) return "—";
    return h < 48 ? `${h}h` : `${Math.round(h / 24)}d`;
  }, [metrics]);

  return (
    <div className="p-4 sm:p-6">
      <PageHeader
        title="Property ID Review"
        subtitle="Records the Property Brain couldn't match with confidence. Confirm, pick the right property, split out a unit, or mark no match."
        action={
          <button type="button" onClick={() => { load(); loadMetrics(); }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-gray-200 text-gray-700 hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Stat label="Open" value={metrics?.open} />
        <Stat label="Resolved (30 days)" value={metrics?.resolved} />
        <Stat label="Median turnaround" value={turnaround} hint="flag to decision, 30 days" />
        <Stat label="Wrong links fixed" value={metrics?.wrong_links?.total}
          hint={metrics ? `${metrics.wrong_links?.client_facing ?? 0} on client-facing listings` : ""} />
        <Stat label="Recurring" value={metrics?.recurring} hint="came back after closing" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          {STATUS_TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setStatus(t.id)}
              className={`px-3 py-2 text-sm ${status === t.id ? "bg-[#55C5D0] text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <select value={store} onChange={(e) => setStore(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
          {STORE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={reason} onChange={(e) => setReason(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
          {REASON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search address or ID"
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]" />
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          {error ? (
            <div className="p-6 flex items-center gap-2 text-sm text-red-600"><AlertCircle className="w-4 h-4" /> {error}</div>
          ) : loading ? (
            <div className="p-6 text-sm text-gray-400">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-gray-500">
              <Inbox className="w-8 h-8 mx-auto text-gray-300 mb-2" /> Nothing here.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map((it) => (
                <li key={it.id}>
                  <button type="button" onClick={() => setSelected(it)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${selected?.id === it.id ? "bg-cyan-50" : ""}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{it.address || it.object_id}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <Pill className="bg-cyan-50 text-cyan-800">{it.store_label}</Pill>
                          <Pill className={REASON_STYLES[it.reason] || "bg-gray-100 text-gray-700"}>{it.reason_label}</Pill>
                          {it.decision && <Pill className="bg-emerald-50 text-emerald-700">{it.decision_label}</Pill>}
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-500 shrink-0">
                        {it.proposed_id ? <div>→ {it.proposed_id}{it.candidate?.confidence ? ` (${it.candidate.confidence})` : ""}</div> : null}
                        <div>{formatMelbourneDateTime(it.flagged_at)}</div>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
            <span>{count} item{count === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="p-1 rounded disabled:opacity-40" aria-label="Previous page"><ChevronLeft className="w-4 h-4" /></button>
              <span>{page} / {pages}</span>
              <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
                className="p-1 rounded disabled:opacity-40" aria-label="Next page"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
        <div className="lg:col-span-2">
          {selected ? (
            <ReviewDetail item={selected} onChanged={onChanged} onClose={() => setSelected(null)} />
          ) : (
            <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-sm text-gray-500">
              <Fingerprint className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              Pick an item to review it.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyIdReviewPage;
